import { formatMoney, formatDateTime } from './formatters';

const WIDTH = 1080;
const PADDING = 64;
const THEMES = {
  dark: {
    bg: '#031225',
    glow: '#07203d',
    panel: '#07192f',
    panelSoft: '#0a2140',
    panelDeep: '#091b34',
    border: 'rgba(94, 234, 212, 0.12)',
    borderSoft: 'rgba(255,255,255,0.05)',
    heading: '#5EEAD4',
    text: '#F8FAFC',
    muted: '#94A3B8',
    accent: '#38BDF8',
    warningText: '#FCD34D',
    footerRule: 'rgba(255,255,255,0.08)',
  },
  light: {
    bg: '#eef7ff',
    glow: '#dbeafe',
    panel: '#ffffff',
    panelSoft: '#f8fbff',
    panelDeep: '#f4f8ff',
    border: 'rgba(14, 116, 144, 0.18)',
    borderSoft: 'rgba(15, 23, 42, 0.08)',
    heading: '#0891b2',
    text: '#0f172a',
    muted: '#64748b',
    accent: '#0284c7',
    warningText: '#b45309',
    footerRule: 'rgba(15,23,42,0.12)',
  },
};

function resolveTheme(theme) {
  return theme === 'light' ? THEMES.light : THEMES.dark;
}


function safeNumber(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
}

function uniqueLines(values) {
  const seen = new Set();
  return values
    .flatMap((v) => String(v || '').split(/\s*\n\s*/))
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .filter((v) => {
      const key = v.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function linePrice(item) {
  const qty = safeNumber(item?.qty ?? item?.quantity ?? 1) || 1;
  const explicit = item?.lineTotal ?? item?.price ?? item?.total;
  if (explicit !== undefined && explicit !== null && explicit !== '') return safeNumber(explicit);
  return safeNumber(item?.unitPrice) * qty;
}

function normalizeCommaAddress(value) {
  const parts = String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const seen = new Set();
  const deduped = [];
  parts.forEach((part) => {
    const key = part.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(part);
  });
  return deduped.join(', ');
}

function addressFrom(order) {
  const raw = order?.raw || {};
  const customer = raw.customer || {};
  const candidates = uniqueLines([
    customer.addressLine1,
    customer.addressLine2,
    raw.address,
    order?.address,
  ]).map(normalizeCommaAddress).filter(Boolean);

  if (!candidates.length) return '';

  const longest = candidates.sort((a, b) => b.length - a.length)[0];
  return normalizeCommaAddress(longest);
}

function paymentLabelFrom(order) {
  const raw = order?.raw || {};
  const label = raw?.payment?.label || raw?.payment?.method || order?.paymentMethod || 'Unknown';
  return String(label).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function createdAtLabel(order) {
  return formatDateTime(order?.createdAt || order?.placedAt || order?.raw?.createdAt || new Date());
}

function sentToStoreLabel(order) {
  const raw = order?.raw || {};
  return formatDateTime(raw?.timings?.sentToStoreAt || raw?.routing?.sentToStoreAt || raw?.updatedAt || new Date());
}

export function buildStoreHandoffCardData(order) {
  const raw = order?.raw || {};
  const pricing = raw.pricing || {};
  const items = Array.isArray(raw.items) && raw.items.length ? raw.items : (order?.items || []);
  const subtotal = safeNumber(pricing.subtotal ?? items.reduce((sum, item) => sum + linePrice(item), 0));
  const deliveryFee = safeNumber(pricing.deliveryFee ?? raw.deliveryFee ?? 0);
  const total = safeNumber(pricing.total ?? raw.total ?? subtotal + deliveryFee);
  const note = raw?.notes?.online || raw?.notes?.customerVisible || order?.notes || '';
  const onlineName = order?.assignedOnline || raw?.assignedOnline || raw?.assignment?.onlineName || raw?.assignment?.onlineUserId || raw?.onlineOwner || '';
  const flagged = Boolean(raw?.issueFlag || raw?.flags?.hasStockIssue || order?.issueFlag);

  return {
    orderNumber: order?.orderNumber || order?.id || 'LINK',
    sentToStore: sentToStoreLabel(order),
    createdAt: createdAtLabel(order),
    customerName: order?.customerName || raw?.customer?.name || 'Customer',
    phone: order?.customerPhone || raw?.customer?.phone || '',
    address: addressFrom(order),
    assignedStore: order?.store || raw?.store || raw?.storeName || 'Unassigned store',
    onlineName,
    paymentLabel: paymentLabelFrom(order),
    subtotal,
    deliveryFee,
    total,
    items: items.map((item, index) => ({
      id: item?.lineId || `${order?.id || 'order'}-${index}`,
      qty: String(item?.qty ?? item?.quantity ?? 1),
      name: item?.name || item?.title || item?.sku || `Item ${index + 1}`,
      lineTotal: linePrice(item),
    })),
    note,
    attention: flagged ? 'Attention: issue already flagged on this order.' : '',
  };
}

function roundedRect(ctx, x, y, width, height, radius, fill, stroke) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${current} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) current = next;
    else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
}

function drawLines(ctx, lines, x, y, lineHeight, color, maxLines = lines.length) {
  ctx.fillStyle = color;
  lines.slice(0, maxLines).forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight);
  });
}

function ellipsizeLine(ctx, text, maxWidth) {
  const clean = String(text || '');
  if (ctx.measureText(clean).width <= maxWidth) return clean;
  let out = clean;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) out = out.slice(0, -1);
  return `${out}…`;
}

function estimateHeight(card, ctx) {
  const addressLines = Math.min(3, wrapText(ctx, card.address, 370).length || 1);
  const itemRows = Math.max(1, card.items.length);
  const noteLineCount = card.note ? Math.max(1, wrapText(ctx, `Customer note: ${card.note}`, 840).length) : 0;
  const attentionLineCount = card.attention ? Math.max(1, wrapText(ctx, card.attention, 840).length) : 0;
  const noteRows = noteLineCount + attentionLineCount;
  return 920 + (addressLines * 20) + (itemRows * 54) + (noteRows ? 28 + (noteRows * 28) : 0);
}

export async function renderStoreHandoffCard(order, options = {}) {
  const colors = resolveTheme(options.theme || order?.raw?.storeHandoffCardTheme || order?.storeHandoffCardTheme || 'dark');
  const measure = document.createElement('canvas').getContext('2d');
  measure.font = '500 24px Inter, Arial, sans-serif';
  const card = buildStoreHandoffCardData(order);
  const height = estimateHeight(card, measure);
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const gradient = ctx.createLinearGradient(0, 0, WIDTH, height);
  gradient.addColorStop(0, colors.bg);
  gradient.addColorStop(1, colors.glow);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, height);

  roundedRect(ctx, PADDING, PADDING, WIDTH - (PADDING * 2), height - (PADDING * 2), 36, colors.panel, colors.border);

  const innerX = PADDING + 36;
  const innerW = WIDTH - (PADDING * 2) - 72;
  let y = PADDING + 52;

  ctx.fillStyle = colors.heading;
  ctx.font = '700 27px Inter, Arial, sans-serif';
  ctx.fillText('LINK', innerX, y);

  y += 54;
  ctx.fillStyle = colors.text;
  ctx.font = '800 44px Inter, Arial, sans-serif';
  ctx.fillText('NEW ORDER', innerX, y);

  y += 54;
  ctx.font = '700 30px Inter, Arial, sans-serif';
  ctx.fillText(`Order: ${card.orderNumber}`, innerX, y);

  y += 38;
  ctx.fillStyle = colors.muted;
  ctx.font = '500 22px Inter, Arial, sans-serif';
  ctx.fillText(`Sent to store: ${card.sentToStore}`, innerX, y);

  const colGap = 24;
  const leftW = 484;
  const rightW = innerW - leftW - colGap;
  const panelY = y + 30;
  const panelH = 246;

  roundedRect(ctx, innerX, panelY, leftW, panelH, 24, colors.panelSoft, colors.borderSoft);
  roundedRect(ctx, innerX + leftW + colGap, panelY, rightW, panelH, 24, colors.panelDeep, colors.borderSoft);

  ctx.fillStyle = colors.heading;
  ctx.font = '700 18px Inter, Arial, sans-serif';
  ctx.fillText('CUSTOMER', innerX + 22, panelY + 32);
  ctx.fillText('FULFILMENT', innerX + leftW + colGap + 22, panelY + 32);

  ctx.fillStyle = colors.text;
  ctx.font = '700 26px Inter, Arial, sans-serif';
  ctx.fillText(ellipsizeLine(ctx, card.customerName, leftW - 44), innerX + 22, panelY + 82);

  ctx.fillStyle = colors.accent;
  ctx.font = '600 20px Inter, Arial, sans-serif';
  ctx.fillText(card.phone || 'No phone saved', innerX + 22, panelY + 118);

  ctx.fillStyle = colors.text;
  ctx.font = '500 19px Inter, Arial, sans-serif';
  const addressLines = wrapText(ctx, card.address, leftW - 44);
  drawLines(ctx, addressLines, innerX + 22, panelY + 156, 26, colors.text, 3);

  const rightX = innerX + leftW + colGap + 22;
  const valueX = rightX + 140;
  const labelYs = [panelY + 82, panelY + 128, panelY + 174, panelY + 220];
  ctx.fillStyle = colors.heading;
  ctx.font = '600 20px Inter, Arial, sans-serif';
  ctx.fillText('Store', rightX, labelYs[0]);
  ctx.fillText('Online', rightX, labelYs[1]);
  ctx.fillText('Payment', rightX, labelYs[2]);
  ctx.fillText('Created', rightX, labelYs[3]);

  ctx.fillStyle = colors.text;
  ctx.font = '700 20px Inter, Arial, sans-serif';
  ctx.fillText(ellipsizeLine(ctx, card.assignedStore, rightW - 170), valueX, labelYs[0]);
  ctx.fillText(ellipsizeLine(ctx, card.onlineName || '—', rightW - 170), valueX, labelYs[1]);
  ctx.fillText(ellipsizeLine(ctx, card.paymentLabel, rightW - 170), valueX, labelYs[2]);
  ctx.font = '500 19px Inter, Arial, sans-serif';
  ctx.fillText(ellipsizeLine(ctx, card.createdAt, rightW - 170), valueX, labelYs[3]);

  y = panelY + panelH + 30;
  const itemsBoxY = y;
  const itemsBoxH = 72 + (Math.max(1, card.items.length) * 52);
  roundedRect(ctx, innerX, itemsBoxY, innerW, itemsBoxH, 24, colors.panelSoft, colors.borderSoft);

  ctx.fillStyle = colors.heading;
  ctx.font = '700 18px Inter, Arial, sans-serif';
  ctx.fillText('ITEMS', innerX + 22, itemsBoxY + 32);

  const headY = itemsBoxY + 58;
  ctx.fillStyle = colors.muted;
  ctx.font = '600 15px Inter, Arial, sans-serif';
  ctx.fillText('QTY', innerX + 22, headY);
  ctx.fillText('ITEM', innerX + 98, headY);
  ctx.textAlign = 'right';
  ctx.fillText('LINE TOTAL', innerX + innerW - 22, headY);
  ctx.textAlign = 'left';

  let rowY = headY + 34;
  card.items.forEach((item, index) => {
    if (index > 0) {
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath();
      ctx.moveTo(innerX + 22, rowY - 24);
      ctx.lineTo(innerX + innerW - 22, rowY - 24);
      ctx.stroke();
    }

    ctx.fillStyle = colors.text;
    ctx.font = '700 20px Inter, Arial, sans-serif';
    ctx.fillText(String(item.qty), innerX + 22, rowY);

    ctx.font = '500 19px Inter, Arial, sans-serif';
    const maxItemWidth = innerW - 278;
    ctx.fillText(ellipsizeLine(ctx, item.name, maxItemWidth), innerX + 98, rowY);

    ctx.textAlign = 'right';
    ctx.font = '700 18px Inter, Arial, sans-serif';
    ctx.fillText(formatMoney(item.lineTotal), innerX + innerW - 22, rowY);
    ctx.textAlign = 'left';
    rowY += 52;
  });

  y = itemsBoxY + itemsBoxH + 28;
  const totalsBoxH = 214;
  roundedRect(ctx, innerX, y, innerW, totalsBoxH, 24, colors.panelSoft, colors.borderSoft);

  ctx.fillStyle = colors.heading;
  ctx.font = '700 18px Inter, Arial, sans-serif';
  ctx.fillText('TOTALS', innerX + 22, y + 32);

  const valueRightX = innerX + innerW - 22;
  const totalRows = [
    { label: 'Sub-Total', value: formatMoney(card.subtotal), strong: false, y: y + 76 },
    { label: 'Delivery', value: formatMoney(card.deliveryFee), strong: false, y: y + 122 },
    { label: 'Order Total', value: formatMoney(card.total), strong: true, y: y + 172 },
  ];

  totalRows.forEach((row) => {
    ctx.textAlign = 'left';
    ctx.fillStyle = row.strong ? colors.text : colors.muted;
    ctx.font = row.strong ? '700 23px Inter, Arial, sans-serif' : '600 19px Inter, Arial, sans-serif';
    ctx.fillText(row.label, innerX + 22, row.y);
    ctx.textAlign = 'right';
    ctx.fillStyle = colors.text;
    ctx.font = row.strong ? '800 30px Inter, Arial, sans-serif' : '700 19px Inter, Arial, sans-serif';
    ctx.fillText(row.value, valueRightX, row.y);
  });

  ctx.textAlign = 'left';
  ctx.fillStyle = colors.muted;
  ctx.font = '600 19px Inter, Arial, sans-serif';
  ctx.fillText('Payment', innerX + 22, y + 204);
  ctx.textAlign = 'right';
  ctx.fillStyle = colors.text;
  ctx.font = '700 19px Inter, Arial, sans-serif';
  ctx.fillText(card.paymentLabel, valueRightX, y + 204);
  ctx.textAlign = 'left';

  y += totalsBoxH + 24;
  if (card.note || card.attention) {
    const lines = [];
    if (card.note) lines.push(...wrapText(ctx, `Customer note: ${card.note}`, innerW - 8));
    if (card.attention) lines.push(...wrapText(ctx, card.attention, innerW - 8));
    ctx.font = '700 18px Inter, Arial, sans-serif';
    drawLines(ctx, lines, innerX, y + 10, 28, colors.warningText);
    y += (lines.length * 28) + 18;
  }

  ctx.strokeStyle = colors.footerRule;
  ctx.beginPath();
  ctx.moveTo(innerX, y);
  ctx.lineTo(innerX + innerW, y);
  ctx.stroke();
  ctx.fillStyle = colors.muted;
  ctx.font = '600 17px Inter, Arial, sans-serif';
  ctx.fillText('Packed via LINK', innerX, y + 30);

  return { canvas, card };
}

export async function cardBlobFromOrder(order, options = {}) {
  const { canvas } = await renderStoreHandoffCard(order, options);
  return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png', 1));
}

export async function copyStoreHandoffCardImage(order, options = {}) {
  const blob = await cardBlobFromOrder(order, options);
  if (!blob) throw new Error('Card image could not be created.');
  if (!navigator.clipboard || typeof window.ClipboardItem === 'undefined') {
    throw new Error('Clipboard image copy is not supported in this browser.');
  }
  await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
}

export async function downloadStoreHandoffCard(order, options = {}) {
  const blob = await cardBlobFromOrder(order, options);
  if (!blob) throw new Error('Card image could not be created.');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${buildStoreHandoffCardData(order).orderNumber || 'link-order'}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function previewStoreHandoffCard(order, options = {}) {
  const blob = await cardBlobFromOrder(order, options);
  if (!blob) throw new Error('Card image could not be created.');
  return URL.createObjectURL(blob);
}

export function openWhatsAppWeb() {
  window.open('https://web.whatsapp.com/', '_blank', 'noopener,noreferrer');
}
